import { useMemo } from "react";

import {
    type IAnalyticsEnrichers,
    type IBtAnalyticsPlugin,
    type ISawmillV3Config,
    sawmillV3Plugin,
} from "@btnet/utilities-analytics";
import { mobile } from "@btnet/utilities-webview";

import { type GlobalInfo } from "helpers/AppProvider.types";

import { reportMessage } from "utilities/errorHelpers";
import { getSelectedJobId } from "utilities/jobPicker/jobPicker";
import { getClientOs } from "utilities/userAgent/getClientOs";
import { getMobileDeviceType } from "utilities/userAgent/userAgent";

export function deriveImpersonation(userInfo: {
    isImpersonatingBuilder: boolean;
    isBuilderImpersonatingOwner: boolean;
}): string | null {
    if (userInfo.isImpersonatingBuilder) {
        return "BTAdmin";
    }
    if (userInfo.isBuilderImpersonatingOwner) {
        return "BuilderImpersonatingOwner";
    }
    return null;
}

/**
 * The analytics package requires every config field, while the backend may omit
 * any of them. Defaults are applied here rather than in the package so the
 * package's config type stays fully-specified for every plugin author.
 */
function coerceSawmillV3Config(
    config: GlobalInfo["appDefaultInfo"]["sawmillV3Configuration"],
): ISawmillV3Config {
    return {
        frontendEnabled: config?.frontendEnabled ?? false,
        ingestionEndpoint: config?.ingestionEndpoint ?? "",
        frontendTenantId: config?.frontendTenantId ?? "",
        frontendBatchThreshold: config?.frontendBatchThreshold ?? 10,
        frontendMaxCachedEvents: config?.frontendMaxCachedEvents ?? 999,
        frontendMaxPayloadSizeBytes: config?.frontendMaxPayloadSizeBytes ?? 500_000,
        frontendFlushIntervalMs: config?.frontendFlushIntervalMs ?? 300_000,
    };
}

/**
 * Exported separately from the hook so the enricher wiring — which is what
 * actually decides the identity fields on every emitted event — can be asserted
 * without mounting a provider tree.
 */
export function buildAnalyticsEnrichers(
    userInfo: GlobalInfo["userInfo"],
    builderId: number | null,
): IAnalyticsEnrichers {
    return {
        getUserOrgLinkId: () => userInfo.globalUserId ?? null,
        getBuilderId: () => builderId,
        getClient: () => {
            if (mobile.isWebview) {
                return "mobile_webview";
            }

            switch (getMobileDeviceType()) {
                case "mobile":
                    return "mobile_browser";
                case "tablet":
                    return "tablet_browser";
                default:
                    return "desktop_browser";
            }
        },
        getClientOs: () => getClientOs(),
        getImpersonation: () => deriveImpersonation(userInfo),
        // Read at emit time, not captured here. The job picker writes its
        // selection to localStorage and nothing re-renders AppProvider when it
        // changes, so a value snapshotted at build time would go stale for the
        // rest of the session.
        //
        // Sentinels (AllJobs 0, NoJobs -1, GlobalDocs -2, GlobalJob -4) go
        // through unchanged rather than being nulled: product analytics wants to
        // tell "the user was on All Jobs" apart from "a single job was
        // selected", and that distinction is exactly what the sentinel carries.
        // Downstream must not treat this as a foreign key without filtering.
        //
        // builderId is nullable upstream (AppProvider passes
        // builderInfo?.builderId ?? null); 0 matches no stored selection, so the
        // helper returns NoJobs, which is the honest answer before a builder is
        // resolved.
        getSelectedJobId: () => getSelectedJobId(builderId ?? 0),
    };
}

/**
 * Builds the plugin set handed to `<AnalyticsProvider>`.
 *
 * Construction lives in Clients.App rather than in the analytics package by
 * design: the package publishes the `IBtAnalyticsPlugin` contract and stays
 * unaware of BTNet's config shape, user model, and webview/user-agent helpers.
 * Adding a second sink (Mixpanel, Segment, an in-house collector) means
 * appending to the array returned here — no change to the package.
 *
 * A disabled sink is omitted from the array rather than constructed and gated
 * internally. `IBtAnalyticsPlugin` has no `enabled` flag on purpose: "disabled"
 * means different things per sink (a backend config flag here, consent state or
 * sampling elsewhere), and a plugin that exists but no-ops still costs a flush
 * timer, a devtools mount, and a `track` call per event. Filtering here costs
 * nothing. Each sink decides its own condition below.
 *
 * The returned array is memoized because `AnalyticsProvider` keys its analytics
 * instance, per-plugin flush timers, and dispose effects off its identity.
 */
export function useAnalyticsPlugins(
    appDefaultInfo: GlobalInfo["appDefaultInfo"],
    userInfo: GlobalInfo["userInfo"],
    builderId: number | null,
): readonly IBtAnalyticsPlugin[] {
    const config = useMemo(
        () => coerceSawmillV3Config(appDefaultInfo.sawmillV3Configuration),
        [appDefaultInfo.sawmillV3Configuration],
    );

    const enrichers = useMemo(
        () => buildAnalyticsEnrichers(userInfo, builderId),
        [userInfo, builderId],
    );

    return useMemo(() => {
        const plugins: IBtAnalyticsPlugin[] = [];

        if (config.frontendEnabled) {
            plugins.push(
                sawmillV3Plugin({
                    config,
                    enrichers,
                    reportError: reportMessage,
                    reportMessage,
                }),
            );
        }

        return plugins;
    }, [config, enrichers]);
}
